import { ChangeDetectorRef, Component, ElementRef, ErrorHandler, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DwComponent, DwComponentType, DwRoutingService } from '@devwareapps/devware-cap';
import { CourseLessonEntity, CourseModuleEntity, LessonTypeAllItems } from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../services/course-repository.service';
import { CourseNavigationService } from '../../services/course-navigation.service';
import {
  CourseProgressItem, LessonProgressItem, ModuleProgressItem
} from '../../models/course-progress-hierarchy.model';
import { enhanceAiSummaryHtml } from '../../utils/enhance-ai-summary-html';
import { ModuleVideoPlaybackService } from '../../../videos/services/module-video-playback.service';

type LessonState = 'completed' | 'in-progress' | 'not-started' | 'preview';
type ModuleTab = 'lessons' | 'ai-summary';
// Per-instance prefix for the tab/panel id pairs below.
let nextTabsInstanceId = 0;

// TEMP: placeholder rendered when ?fakeAi=1 is in the URL. Lets us preview
// the AI Summary tab on any module before real StudentModuleSummary rows are
// generated for the dev DB. Remove this constant and the shouldShowFakeAiSummary
// branch in setupModule() once real summaries are flowing.
const FAKE_AI_SUMMARY_HTML = `
  <div class="ai-summary-container">
    <div class="ai-section">
      <div class="ai-section-title">Module Summary</div>
      <div class="ai-section-body">
        <ul>
          <li>Total Time Spent: Approximately 6 minutes</li>
          <li>Video Time: About 1 minute 50 seconds</li>
          <li>Quiz Time: About 4 minutes 18 seconds</li>
          <li>Last Activity Date: May 4, 2026</li>
          <li>Lessons Completed: 3 of 7</li>
        </ul>
      </div>
    </div>
    <div class="ai-section">
      <div class="ai-section-title">Insights</div>
      <div class="ai-section-body">
        <p>Strong foundation on materials and Foreflight setup. Quizzes flagged a couple of gaps in <strong>medical certificates</strong> and <strong>aircraft resources</strong>.</p>
        <p>Reviewing these specific topics will ensure confident interpretation during practical operations.</p>
      </div>
    </div>
    <div class="ai-section">
      <div class="ai-section-title">Study Advice</div>
      <div class="ai-section-body">
        <p>Re-watch Lesson 1.7 before moving into Module 2. The certificate types map directly onto the aircraft systems content covered there.</p>
      </div>
    </div>
  </div>
`;

interface LessonRow {
  ref: LessonProgressItem;
  state: LessonState;
  statusLabel: string;     // "Completed" | "In progress" | "Not started" | "Preview"
  metaText: string;        // "Completed 04/22/2026" | "Last activity 04/28/2026" | ""
  detailLabel: 'ATTEMPTS' | 'RUNTIME';
  detailValue: string;     // "1 / 2" passed/attempts | "18:24" runtime | "—"
}

interface FilterChip {
  key: 'all' | 'in-progress' | 'completed' | 'not-started';
  altKey?: 'preview';
  label: string;
  count: number;
  active: boolean;
}

@DwComponent({
  key: 'ModuleDetailPage',
  name: 'Module Detail Page',
  componentType: DwComponentType.pageComponent,
  isGlobal: true
})
@Component({
  selector: 'app-module-detail-view',
  templateUrl: './module-detail-view.component.html',
  styleUrls: ['./module-detail-view.component.scss']
})
export class ModuleDetailViewComponent implements OnInit, OnDestroy {
  @ViewChild('mdTabs') private mdTabsRef?: ElementRef<HTMLElement>;
  module: CourseModuleEntity | null = null;
  moduleProgress: ModuleProgressItem | null = null;
  // Set when the read errors, returns no course progress, or the module isn't
  // in the course, so the template swaps the skeleton for Retry.
  loadFailed = false;
  courseProgress: CourseProgressItem | null = null;

  totalRuntimeLabel = '';   // "2h 53m"
  progressPercent = 0;
  lessonsCompleted = 0;
  lessonsTotal = 0;
  courseName = '';          // Used by the breadcrumb above the module title.

  lessons: LessonRow[] = [];
  allLessons: LessonRow[] = [];
  chips: FilterChip[] = [];

  nextModule: CourseModuleEntity | null = null;
  previousModule: CourseModuleEntity | null = null;

  // Hero description is clamped to a few lines by default so the hero
  // height stays predictable; this flag unclamps it when the reader taps
  // Show more. Applied at all viewport sizes, not just mobile.
  descriptionExpanded = false;

  // Lessons / AI Summary tab strip lives in the lessons card. The AI Summary
  // tab only renders when an aiSummaryHtml is present for the current student.
  activeTab: ModuleTab = 'lessons';
  // activePanel mirrors activeTab one frame later. The FLIP pill animation is
  // composited (transform-only), but the AI Summary panel mounts a large
  // <dw-html-view> on first toggle which blocks the main thread long enough
  // to push the first compositor commit past the 280ms animation, making the
  // pill appear to teleport. Driving the panel *ngIf off this deferred copy
  // lets the FLIP commit before the heavy parse begins.
  activePanel: ModuleTab = 'lessons';
  // Stable tab <-> panel ids for aria-controls / aria-labelledby.
  readonly tabIds = (n => ({
    lessonsTab: `md-tab-lessons-${n}`, lessonsPanel: `md-panel-lessons-${n}`,
    aiTab: `md-tab-ai-summary-${n}`, aiPanel: `md-panel-ai-summary-${n}`
  }))(++nextTabsInstanceId);
  aiSummaryHtml: string | null = null;

  get hasAISummary(): boolean {
    return !!this.aiSummaryHtml;
  }

  private destroy$ = new Subject<void>();
  private loadSubscription?: Subscription;

  constructor(
    private router: Router,
    private dwRoutingService: DwRoutingService,
    private courseRepositoryService: CourseRepositoryService,
    private courseNavigationService: CourseNavigationService,
    private moduleVideoPlaybackService: ModuleVideoPlaybackService,
    private cdr: ChangeDetectorRef,
    private errorHandler: ErrorHandler,
  ) { }

  ngOnInit(): void {
    this.setupModule();

    // DevWare's getRoutesChanges fires for every URL change including
    // in-app metadata-driven transitions that don't always emit a standard
    // NavigationEnd. The course-navigation sidebar uses the same pattern.
    this.dwRoutingService.getRoutesChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.setupModule());
  }

  //private loadFromUrl(url: string): void {
  private setupModule(): void {
    const lessonReferenceInfo = this.courseNavigationService.getCurrentCourseParameters();


    // // URL pattern: /my-courses/view/:courseId/module/:moduleId
    // const match = url.match(/^\/my-courses\/view\/(\d+)\/module\/(\d+)/);
    // if (!match) {
    //   this.module = null;
    //   return;
    // }

    const courseId = lessonReferenceInfo.courseId;
    const moduleId = lessonReferenceInfo.moduleId;

    // Clear current module on navigation so the skeleton renders while the
    // next module's data is loading. Without this, in-place URL changes
    // (e.g. clicking "Next module") would keep showing the previous module's
    // content until the new course-progress arrives.
    if (moduleId !== this.pendingModuleId) {
      this.module = null;
      this.moduleProgress = null;
      this.lessons = [];
      this.chips = [];
      this.nextModule = null;
      this.previousModule = null;
      this.aiSummaryHtml = null;
      // Snap back to Lessons when navigating between modules — the new module
      // may not have an AI summary, and even if it does we want the reader to
      // see the lesson list first.
      this.activeTab = 'lessons';
      this.activePanel = 'lessons';
    }
    this.pendingModuleId = moduleId;
    this.loadFailed = false;

    // Drop the previous read so a late response/error from an older module
    // can't land on this one.
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.courseRepositoryService.getCourseProgress(courseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: progress => {
        this.courseProgress = progress;
        this.moduleProgress = progress?.modules?.find(
          m => m.module.CourseModuleId === this.pendingModuleId
        ) ?? null;
        this.module = this.moduleProgress?.module ?? null;
        this.loadFailed = !this.module;
        if (this.loadFailed) { return; }

        const studentCourseId = progress?.studentCourse?.StudentCourseId;
        const hasVideoLessons = this.moduleProgress?.lessons?.some(
          lesson => lesson.lesson?.LessonTypeId === LessonTypeAllItems.Video
        );
        if (studentCourseId && this.module?.CourseModuleId && hasVideoLessons) {
          this.moduleVideoPlaybackService.preload(studentCourseId, this.module.CourseModuleId);
        }
        const rawSummary = this.moduleProgress?.aiSummaryHtml
          || (this.shouldShowFakeAiSummary() ? FAKE_AI_SUMMARY_HTML : null);
        // TEMP: shouldShowFakeAiSummary() is a dev-only fallback so the AI
        // Summary tab is reachable while real rows aren't in the DB yet.
        // Remove that branch (and FAKE_AI_SUMMARY_HTML) once real summaries flow.
        this.aiSummaryHtml = enhanceAiSummaryHtml(rawSummary);
        // Same-module reload (Retry) can drop the summary; don't leave the
        // selection on a tab that no longer renders.
        if (!this.hasAISummary) {
          this.activeTab = 'lessons';
          this.activePanel = 'lessons';
        }

        this.applyHeader();
        this.buildlessons();
        this.buildChips();
        this.resolveSiblingModules();
      }, error: err => {
        this.module = null;
        this.loadFailed = true;
        // Devware's global handler logs (console/server) without a toast;
        // the inline Retry state is the user-facing message.
        this.errorHandler.handleError(err);
      } });
  }

  retryLoad(): void {
    this.setupModule();
  }

  toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  selectTab(tab: ModuleTab): void {
    if (tab === 'ai-summary' && !this.hasAISummary) {
      return;
    }

    this.flipPill(this.mdTabsRef?.nativeElement, '.md-tab-pill', () => {
      this.activeTab = tab;
    });
    // Defer the panel swap one frame so the composited FLIP can commit before
    // the heavy <dw-html-view> parse blocks the main thread. Without this,
    // the first toggle to AI Summary appears to teleport because the first
    // compositor commit lands ~5s after click on a fresh module.
    requestAnimationFrame(() => {
      if (this.activePanel === tab) { return; }
      this.activePanel = tab;
      this.cdr.detectChanges();
    });
  }

  // ARIA tabs keyboard pattern for the roving tabindex above: arrows cycle,
  // Home/End jump; selection follows focus.
  onTabKeydown(event: KeyboardEvent): void {
    const tabs: ModuleTab[] = this.hasAISummary ? ['lessons', 'ai-summary'] : ['lessons'];
    const current = Math.max(tabs.indexOf(this.activeTab), 0);
    let next: number;
    switch (event.key) {
      case 'ArrowRight': next = (current + 1) % tabs.length; break;
      case 'ArrowLeft': next = (current - 1 + tabs.length) % tabs.length; break;
      case 'Home': next = 0; break;
      case 'End': next = tabs.length - 1; break;
      default: return;
    }
    event.preventDefault();
    if (tabs[next] !== this.activeTab) {
      this.selectTab(tabs[next]);
    }
    // selectTab runs CD synchronously, so the new tab already has tabindex 0.
    this.mdTabsRef?.nativeElement.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  }

  // FLIP: First/Last/Invert/Play. Capture the active pill's rect BEFORE the
  // state swap, mutate, force CD so Angular has moved the *ngIf'd pill onto
  // the new active button, capture the new pill's rect, then play a Web
  // Animation that starts at the inverse transform (visually pinned to the
  // OLD pill's rect) and ends at identity (the new natural position). Only
  // the pill element animates, so labels stay crisp.
  private flipPill(
    strip: HTMLElement | undefined,
    pillSelector: string,
    mutate: () => void,
  ): void {
    const oldPill = strip?.querySelector<HTMLElement>(pillSelector);
    const oldRect = oldPill?.getBoundingClientRect();

    mutate();
    // Synchronous CD so the *ngIf-driven pill is removed from the old active
    // button and added to the new one before we measure newRect below.
    this.cdr.detectChanges();

    if (!oldRect || !strip) { return; }
    const newPill = strip.querySelector<HTMLElement>(pillSelector);
    if (!newPill || newPill === oldPill) { return; }
    if (this.prefersReducedMotion()) { return; }

    const newRect = newPill.getBoundingClientRect();
    const dx = oldRect.left - newRect.left;
    const dy = oldRect.top - newRect.top;
    const sx = newRect.width === 0 ? 1 : oldRect.width / newRect.width;
    const sy = newRect.height === 0 ? 1 : oldRect.height / newRect.height;

    newPill.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, transformOrigin: '0 0' },
        { transform: 'none', transformOrigin: '0 0' },
      ],
      { duration: 280, easing: 'ease-in-out', fill: 'none' },
    );
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  // TEMP: see FAKE_AI_SUMMARY_HTML at the top of this file. Remove with that
  // constant once real StudentModuleSummary data is available in the dev DB.
  //
  // The flag is sticky for the session so it survives intra-app navigation
  // (course-nav sidebar, Next/Previous module buttons) — those rebuild URLs
  // without preserving query strings, which would otherwise wipe the dev
  // affordance every time you change modules. To turn it off, append
  // ?fakeAi=0 to any module URL or clear the tab's sessionStorage.
  private shouldShowFakeAiSummary(): boolean {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('fakeAi')) {
        const enable = params.get('fakeAi') !== '0';
        if (enable) {
          window.sessionStorage.setItem('aviator.fakeAi', '1');
        } else {
          window.sessionStorage.removeItem('aviator.fakeAi');
        }
        return enable;
      }
      return window.sessionStorage.getItem('aviator.fakeAi') === '1';
    } catch {
      return false;
    }
  }

  navigateToNextModule(): void {
    if (!this.nextModule) { return; }
    this.courseNavigationService.navigateToModule(
      this.nextModule.CourseId,
      this.nextModule.CourseModuleId,
    );
  }

  navigateToPreviousModule(): void {
    if (!this.previousModule) { return; }
    this.courseNavigationService.navigateToModule(
      this.previousModule.CourseId,
      this.previousModule.CourseModuleId,
    );
  }

  private resolveSiblingModules(): void {
    const modules = this.courseProgress?.modules ?? [];
    const currentOrder = this.module?.ModuleOrder;
    if (currentOrder == null) {
      this.nextModule = null;
      this.previousModule = null;
      return;
    }

    // Pick the lowest ModuleOrder strictly greater than the current, and the
    // highest strictly less. The course-progress array isn't guaranteed sorted,
    // so we sweep both directions in a single pass.
    let next: CourseModuleEntity | null = null;
    let prev: CourseModuleEntity | null = null;
    for (const item of modules) {
      const order = item.module?.ModuleOrder;
      if (order == null) { continue; }
      if (order > currentOrder && (!next || order < next.ModuleOrder!)) {
        next = item.module;
      }
      if (order < currentOrder && (!prev || order > prev.ModuleOrder!)) {
        prev = item.module;
      }
    }
    this.nextModule = next;
    this.previousModule = prev;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackRow = (_: number, row: LessonRow) => row.ref.lesson.CourseLessonId;

  selectChip(chipKey: FilterChip['key']): void {
    this.chips = this.chips.map(c => ({ ...c, active: c.key === chipKey }));
    this.persistChip(chipKey);
    this.filterLessons();
  }

  navigateToLesson(row: LessonRow): void {
    const lesson = row.ref.lesson;
    if (lesson.IsPreview || !this.module) {
      return;
    }
    this.courseNavigationService.navigateToLesson({
      courseId: this.module.CourseId,
      moduleId: this.module.CourseModuleId,
      lessonId: lesson.CourseLessonId,
    });
  }

  navigateToCourse(): void {
    // Falls back to the route's course id so the unavailable state can still go back.
    const courseId = this.module?.CourseId ?? this.courseNavigationService.getCurrentCourseParameters()?.courseId;
    if (!courseId) { return; }
    this.courseNavigationService.navigateToCourse(courseId);
  }

  protected isQuizLesson(lesson: CourseLessonEntity): boolean {
    return lesson?.LessonTypeId === LessonTypeAllItems.Quiz;
  }

  private pendingModuleId = 0;

  private applyHeader(): void {
    if (!this.module) { return; }

    this.totalRuntimeLabel = this.formatHoursMinutes(this.module.TotalModuleTimeSeconds);

    const mp = this.moduleProgress;
    this.lessonsTotal = mp?.totalLessons ?? this.module.CourseLesson?.length ?? 0;
    this.lessonsCompleted = mp?.completedLessons ?? 0;
    const pct = mp?.moduleProgress?.PercentComplete ?? 0;
    this.progressPercent = Math.max(0, Math.min(100, pct));
    this.courseName = this.courseProgress?.course?.CourseName ?? '';
  }

  private buildlessons(): void {
    const lessons = this.moduleProgress?.lessons ?? [];

    this.allLessons = lessons.map(item => {
      const state = this.deriveState(item);
      const isQuizLesson = this.isQuizLesson(item.lesson);
      return {
        ref: item,
        state,
        statusLabel: this.statusLabel(state),
        metaText: this.metaText(item, state),
        detailLabel: isQuizLesson ? 'ATTEMPTS' : 'RUNTIME',
        detailValue: isQuizLesson
          ? this.formatQuizAttempts(item)
          : this.formatMinutesSeconds(item.durationSeconds),
      };
    });

    this.filterLessons();
  }

  private buildChips(): void {
    // Counts come from allLessons so activating a filter doesn't shrink the
    // *other* chips' counts (which would mislead the user).
    const inProgress = this.allLessons.filter(r => r.state === 'in-progress').length;
    const completed = this.allLessons.filter(r => r.state === 'completed').length;
    const notStarted = this.allLessons.filter(r => r.state === 'not-started' || r.state === 'preview').length;

    // Restore the user's previous selection across data refreshes (within this
    // instance) and across navigations (lesson → back). Falls through to "all".
    const previousActive = this.chips.find(c => c.active)?.key ?? this.restoreChipKey();

    this.chips = [
      { key: 'all',          label: 'All',          count: this.allLessons.length, active: false },
      { key: 'in-progress',  label: 'In progress',  count: inProgress,             active: false },
      { key: 'completed',    label: 'Completed',    count: completed,              active: false },
      { key: 'not-started',  altKey: 'preview',     label: 'Not started',
        count: notStarted, active: false },
    ];
    const target = this.chips.find(c => c.key === previousActive) ?? this.chips[0];
    target.active = true;

    // buildlessons → filterLessons ran with stale chip state; re-apply now.
    this.filterLessons();
  }

  // Per-module sessionStorage so the active filter survives navigation into a
  // lesson and back. Keyed by module id, not course id, because each module's
  // state set is independent (you might leave Module 1 on "Completed" but
  // Module 3 on "All").
  private chipStorageKey(): string | null {
    return this.pendingModuleId ? `aviator.md-filter.${this.pendingModuleId}` : null;
  }

  private persistChip(key: FilterChip['key']): void {
    const storageKey = this.chipStorageKey();
    if (!storageKey) { return; }
    try { window.sessionStorage.setItem(storageKey, key); } catch { /* ignore */ }
  }

  private restoreChipKey(): FilterChip['key'] {
    const storageKey = this.chipStorageKey();
    if (!storageKey) { return 'all'; }
    try {
      const value = window.sessionStorage.getItem(storageKey);
      if (value === 'in-progress' || value === 'completed' || value === 'not-started' || value === 'all') {
        return value;
      }
    } catch { /* ignore */ }
    return 'all';
  }

  filterLessons() {
    const activeChip = this.chips.find(c => c.active);
    if (!activeChip || activeChip.key === 'all') {
      this.lessons = [...this.allLessons];
      return;
    }

    this.lessons  = this.allLessons.filter(l => l.state == activeChip.key || l.state == activeChip.altKey);
  }

  private deriveState(item: LessonProgressItem): LessonState {
    if (item.lesson.IsPreview) { return 'preview'; }
    if (item.isComplete) { return 'completed'; }
    if ((item.percentComplete ?? 0) > 0) { return 'in-progress'; }
    if ((item.lessonProgress?.TotalTimeSeconds ?? 0) > 0) { return 'in-progress'; }
    return 'not-started';
  }

  private statusLabel(state: LessonState): string {
    switch (state) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In progress';
      case 'preview': return 'Preview';
      default: return 'Not started';
    }
  }

  private metaText(item: LessonProgressItem, state: LessonState): string {
    if (state === 'completed' && item.lessonProgress?.CompletedDateTime) {
      return `Completed ${this.formatDate(item.lessonProgress.CompletedDateTime)}`;
    }
    if (state === 'in-progress' && item.lessonProgress?.LastActivityDateTime) {
      return `Last activity ${this.formatDate(item.lessonProgress.LastActivityDateTime)}`;
    }
    return '';
  }

  private formatHoursMinutes(totalSeconds?: number): string {
    const s = Math.max(0, Math.round(totalSeconds ?? 0));
    if (s === 0) { return '0m'; }
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    if (hours > 0 && minutes > 0) { return `${hours}h ${minutes}m`; }
    if (hours > 0) { return `${hours}h`; }
    return `${minutes}m`;
  }

  private formatMinutesSeconds(totalSeconds?: number): string {
    const s = Math.max(0, Math.round(totalSeconds ?? 0));
    if (s === 0) { return '—'; }
    const minutes = Math.floor(s / 60);
    const seconds = `${s % 60}`.padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  private formatQuizAttempts(item: LessonProgressItem): string {
    const completedAttemptCount = item.lessonProgress?.CompletedQuizAttemptCount ?? 0;
    if (completedAttemptCount === 0) { return '0'; }

    return `${item.lessonProgress?.PassedQuizAttemptCount ?? 0} / ${completedAttemptCount}`;
  }

  private formatDate(value: string | Date | undefined): string {
    if (!value) { return ''; }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) { return ''; }
    const mm = `${date.getMonth() + 1}`.padStart(2, '0');
    const dd = `${date.getDate()}`.padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
}
